import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ============ PREMIUM ONBOARDING EXPERIENCE ============
// This is NOT a form. This is a guided transformation.
// Tone: emotional, deep, feminine, premium, identity-focused
// Design: black / gold / editorial / minimal

const useSteps = (t) => [
  // STEP 1 — ACCUEIL
  {
    id: 'welcome',
    type: 'welcome',
    title: t('onboarding.welcome.title'),
    content: [
      t('onboarding.welcome.content1'),
      t('onboarding.welcome.content2'),
      t('onboarding.welcome.content3'),
      "",
      t('onboarding.welcome.content4'),
      t('onboarding.welcome.content5'),
      t('onboarding.welcome.content6'),
      "",
      t('onboarding.welcome.content7'),
      t('onboarding.welcome.content8')
    ],
    subtext: `${t('onboarding.welcome.subtext1')}\n${t('onboarding.welcome.subtext2')}\n${t('onboarding.welcome.subtext3')}`,
    button: t('onboarding.welcome.button')
  },
  
  // STEP 2 — IDENTITÉ ÉMOTIONNELLE
  {
    id: 'identity',
    type: 'textarea',
    field: 'identity',
    title: t('onboarding.identity.title'),
    question: t('onboarding.identity.question'),
    subquestion: t('onboarding.identity.subquestion'),
    help: t('onboarding.identity.help'),
    placeholder: t('onboarding.identity.placeholder')
  },
  
  // STEP 3 — CE QUE TU CONSTRUIS (identity_type + business_activity)
  {
    id: 'what_you_build',
    type: 'business_identity',
    title: t('onboarding.whatYouBuild.title'),
    question1: t('onboarding.whatYouBuild.question1'),
    identityOptions: [
      { id: 'entrepreneur', label: t('onboarding.whatYouBuild.options.entrepreneur') },
      { id: 'coach', label: t('onboarding.whatYouBuild.options.coach') },
      { id: 'creator', label: t('onboarding.whatYouBuild.options.creator') },
      { id: 'freelance', label: t('onboarding.whatYouBuild.options.freelance') },
      { id: 'consultant', label: t('onboarding.whatYouBuild.options.consultant') },
      { id: 'artist', label: t('onboarding.whatYouBuild.options.artist') },
      { id: 'therapist', label: t('onboarding.whatYouBuild.options.therapist') },
      { id: 'other', label: t('onboarding.whatYouBuild.options.other'), hasInput: true }
    ],
    question2: t('onboarding.whatYouBuild.question2'),
    placeholder: t('onboarding.whatYouBuild.placeholder'),
    microcopy: t('onboarding.whatYouBuild.microcopy')
  },
  
  // STEP 4 — POUR QUI TU CRÉES (audience)
  {
    id: 'audience',
    type: 'textarea',
    field: 'audience',
    title: t('onboarding.audience.title'),
    question: t('onboarding.audience.question'),
    subquestion: t('onboarding.audience.subquestion'),
    help: t('onboarding.audience.help'),
    placeholder: t('onboarding.audience.placeholder')
  },
  
  // STEP 5 — LÀ OÙ TU EN ES (situation + revenue_level + offer_type)
  {
    id: 'situation',
    type: 'business_situation',
    title: t('onboarding.situation.title'),
    question1: t('onboarding.situation.question1'),
    situationOptions: [
      { id: 'beginning', label: t('onboarding.situation.situationOptions.beginning') },
      { id: 'building_unclear', label: t('onboarding.situation.situationOptions.building_unclear') },
      { id: 'existing_misaligned', label: t('onboarding.situation.situationOptions.existing_misaligned') },
      { id: 'ready_realign', label: t('onboarding.situation.situationOptions.ready_realign') },
      { id: 'other', label: t('onboarding.situation.situationOptions.other'), hasInput: true }
    ],
    question2: t('onboarding.situation.question2'),
    revenueOptions: [
      { id: '0', label: t('onboarding.situation.revenueOptions.0') },
      { id: 'under_1k', label: t('onboarding.situation.revenueOptions.under_1k') },
      { id: '1k_3k', label: t('onboarding.situation.revenueOptions.1k_3k') },
      { id: '3k_5k', label: t('onboarding.situation.revenueOptions.3k_5k') },
      { id: '5k_10k', label: t('onboarding.situation.revenueOptions.5k_10k') },
      { id: '10k_plus', label: t('onboarding.situation.revenueOptions.10k_plus') }
    ],
    question3: t('onboarding.situation.question3'),
    offerOptions: [
      { id: 'services', label: t('onboarding.situation.offerOptions.services') },
      { id: 'coaching', label: t('onboarding.situation.offerOptions.coaching') },
      { id: 'digital', label: t('onboarding.situation.offerOptions.digital') },
      { id: 'physical', label: t('onboarding.situation.offerOptions.physical') },
      { id: 'hybrid', label: t('onboarding.situation.offerOptions.hybrid') },
      { id: 'not_yet', label: t('onboarding.situation.offerOptions.not_yet') }
    ],
    microcopy: t('onboarding.situation.microcopy')
  },
  
  // STEP 6 — VISION
  {
    id: 'vision',
    type: 'textarea',
    field: 'vision',
    title: t('onboarding.vision.title'),
    question: t('onboarding.vision.question'),
    guidance: [
      t('onboarding.vision.guidance1'),
      t('onboarding.vision.guidance2'),
      t('onboarding.vision.guidance3'),
      t('onboarding.vision.guidance4')
    ],
    help: t('onboarding.vision.help'),
    placeholder: t('onboarding.vision.placeholder')
  },
  
  // STEP 7 — BLOCAGES
  {
    id: 'blocks',
    type: 'multiselect',
    field: 'blocks',
    title: t('onboarding.blocks.title'),
    question: t('onboarding.blocks.question'),
    options: [
      { id: 'lack_clarity', label: t('onboarding.blocks.options.lack_clarity') },
      { id: 'self_doubt', label: t('onboarding.blocks.options.self_doubt') },
      { id: 'dont_know_post', label: t('onboarding.blocks.options.dont_know_post') },
      { id: 'compare_too_much', label: t('onboarding.blocks.options.compare_too_much') },
      { id: 'dont_know_create', label: t('onboarding.blocks.options.dont_know_create') },
      { id: 'lack_structure', label: t('onboarding.blocks.options.lack_structure') },
      { id: 'lost_direction', label: t('onboarding.blocks.options.lost_direction') },
      { id: 'dont_know_start', label: t('onboarding.blocks.options.dont_know_start') },
      { id: 'procrastinate', label: t('onboarding.blocks.options.procrastinate') },
      { id: 'other', label: t('onboarding.blocks.options.other'), hasInput: true }
    ],
    microcopy: t('onboarding.blocks.microcopy')
  },
  
  // STEP 8 — POSITIONNEMENT
  {
    id: 'positioning',
    type: 'positioning',
    field: 'positioning',
    title: t('onboarding.positioning.title'),
    question1: t('onboarding.positioning.question1'),
    question2: t('onboarding.positioning.question2'),
    options: [
      { id: 'elegant', label: t('onboarding.positioning.options.elegant') },
      { id: 'magnetic', label: t('onboarding.positioning.options.magnetic') },
      { id: 'powerful', label: t('onboarding.positioning.options.powerful') },
      { id: 'soft', label: t('onboarding.positioning.options.soft') },
      { id: 'luxury', label: t('onboarding.positioning.options.luxury') },
      { id: 'authentic', label: t('onboarding.positioning.options.authentic') },
      { id: 'professional', label: t('onboarding.positioning.options.professional') },
      { id: 'expert', label: t('onboarding.positioning.options.expert') },
      { id: 'other', label: t('onboarding.positioning.options.other'), hasInput: true }
    ],
    microcopy: t('onboarding.positioning.microcopy')
  },
  
  // STEP 9 — UNIVERS VISUEL
  {
    id: 'visual_style',
    type: 'select',
    field: 'visual_style',
    title: t('onboarding.visualStyle.title'),
    question: t('onboarding.visualStyle.question'),
    options: [
      { id: 'minimalist', label: t('onboarding.visualStyle.options.minimalist') },
      { id: 'bold', label: t('onboarding.visualStyle.options.bold') },
      { id: 'soft', label: t('onboarding.visualStyle.options.soft') },
      { id: 'luxury_editorial', label: t('onboarding.visualStyle.options.luxury_editorial') },
      { id: 'modern', label: t('onboarding.visualStyle.options.modern') },
      { id: 'other', label: t('onboarding.visualStyle.options.other'), hasInput: true }
    ],
    microcopy: t('onboarding.visualStyle.microcopy')
  },
  
  // STEP 10 — CONTENU
  {
    id: 'content',
    type: 'content',
    field: 'content',
    title: t('onboarding.content.title'),
    question1: t('onboarding.content.question1'),
    question2: t('onboarding.content.question2'),
    expressionOptions: [
      { id: 'not_yet', label: t('onboarding.content.expressionOptions.not_yet') },
      { id: 'trying', label: t('onboarding.content.expressionOptions.trying') },
      { id: 'present_not_aligned', label: t('onboarding.content.expressionOptions.present_not_aligned') },
      { id: 'regular', label: t('onboarding.content.expressionOptions.regular') },
      { id: 'other', label: t('onboarding.content.expressionOptions.other'), hasInput: true }
    ],
    question3: t('onboarding.content.question3'),
    contentOptions: [
      { id: 'know_what_post', label: t('onboarding.content.contentOptions.know_what_post') },
      { id: 'lost', label: t('onboarding.content.contentOptions.lost') },
      { id: 'delegated', label: t('onboarding.content.contentOptions.delegated') },
      { id: 'image_aligned', label: t('onboarding.content.contentOptions.image_aligned') },
      { id: 'image_not_me', label: t('onboarding.content.contentOptions.image_not_me') },
      { id: 'other', label: t('onboarding.content.contentOptions.other'), hasInput: true }
    ],
    microcopy: t('onboarding.content.microcopy'),
    yesLabel: t('onboarding.content.yes'),
    noLabel: t('onboarding.content.no')
  },
  
  // STEP 11 — OBJECTIF
  {
    id: 'objective',
    type: 'multiselect',
    field: 'objective',
    title: t('onboarding.objective.title'),
    question: t('onboarding.objective.question'),
    options: [
      { id: 'visibility', label: t('onboarding.objective.options.visibility') },
      { id: 'ideal_clients', label: t('onboarding.objective.options.ideal_clients') },
      { id: 'strong_image', label: t('onboarding.objective.options.strong_image') },
      { id: 'realign', label: t('onboarding.objective.options.realign') },
      { id: 'structure', label: t('onboarding.objective.options.structure') },
      { id: 'other', label: t('onboarding.objective.options.other'), hasInput: true }
    ],
    microcopy: t('onboarding.objective.microcopy')
  },
  
  // STEP 12 — ACTIVATION
  {
    id: 'activation',
    type: 'activation',
    title: t('onboarding.activation.title'),
    content: [
      t('onboarding.activation.content1'),
      t('onboarding.activation.content2'),
      "",
      t('onboarding.activation.content3')
    ],
    followup: t('onboarding.activation.followup'),
    subtext: t('onboarding.activation.subtext'),
    button: t('onboarding.activation.button')
  }
];

const Onboarding = () => {
  const { t } = useTranslation();
  const STEPS = useSteps(t);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Emotional identity
    identity: '',
    
    // Business identity (NEW)
    identity_type: '',
    identity_type_other: '',
    business_activity: '',
    
    // Audience (NEW)
    audience: '',
    
    // Situation + Business context
    situation: [],
    situation_other: '',
    revenue_level: '',
    offer_type: '',
    
    // Vision
    vision: '',
    
    // Blocks
    blocks: [],
    blocks_other: '',
    
    // Positioning
    positioning_current: '',
    positioning_desired: [],
    positioning_other: '',
    
    // Visual
    visual_style: '',
    visual_style_other: '',
    
    // Content
    uses_instagram: null,
    content_expression: '',
    content_expression_other: '',
    content_status: [],
    content_status_other: '',
    
    // Objective
    objective: [],
    objective_other: ''
  });
  const [loading, setLoading] = useState(false);
  
  const { token, user, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const step = STEPS[currentStep];
  const totalSteps = STEPS.length;
  const progress = ((currentStep) / (totalSteps - 1)) * 100;

  const canProceed = () => {
    if (step.type === 'welcome' || step.type === 'activation') return true;
    
    switch (step.id) {
      case 'identity':
        return formData.identity.trim().length > 10;
      case 'what_you_build':
        return formData.identity_type !== '' && formData.business_activity.trim().length > 5;
      case 'audience':
        return formData.audience.trim().length > 10;
      case 'situation':
        return formData.situation.length > 0 && formData.revenue_level !== '' && formData.offer_type !== '';
      case 'vision':
        return formData.vision.trim().length > 10;
      case 'blocks':
        return formData.blocks.length > 0;
      case 'positioning':
        return formData.positioning_current.trim().length > 5 && formData.positioning_desired.length > 0;
      case 'visual_style':
        return formData.visual_style !== '';
      case 'content':
        return formData.uses_instagram !== null;
      case 'objective':
        return formData.objective.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleMultiSelect = (field, optionId) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(optionId)) {
        return { ...prev, [field]: current.filter(id => id !== optionId) };
      } else {
        return { ...prev, [field]: [...current, optionId] };
      }
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Structure data for AI - includes both emotional and strategic fields
      const structuredData = {
        // Emotional Identity
        identity: formData.identity,
        
        // Business Identity (NEW)
        identity_type: formData.identity_type,
        identity_type_other: formData.identity_type_other,
        business_activity: formData.business_activity,
        
        // Audience (NEW)
        audience: formData.audience,
        
        // Situation + Business context
        situation: formData.situation,
        situation_other: formData.situation_other,
        revenue_level: formData.revenue_level,
        offer_type: formData.offer_type,
        
        // Vision
        vision: formData.vision,
        
        // Blocks
        blocks: formData.blocks,
        blocks_other: formData.blocks_other,
        
        // Positioning
        positioning_current: formData.positioning_current,
        positioning_desired: formData.positioning_desired,
        positioning_other: formData.positioning_other,
        
        // Visual
        visual_style: formData.visual_style,
        visual_style_other: formData.visual_style_other,
        
        // Content
        uses_instagram: formData.uses_instagram,
        content_expression: formData.content_expression,
        content_expression_other: formData.content_expression_other,
        content_status: formData.content_status,
        content_status_other: formData.content_status_other,
        
        // Objective
        objective: formData.objective,
        objective_other: formData.objective_other
      };

      await axios.post(`${API}/onboarding/save`, structuredData, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      updateUser({ ...user, onboarding_completed: true, onboarding_data: structuredData });
      toast.success('Ta vision prend forme.');
      
      // Beta users and test accounts go directly to profile revelation (no upgrade page)
      // Regular users see the upgrade experience
      const isBetaUser = user?.is_beta_user || user?.is_test_account || user?.subscription_status === 'beta';
      if (isBetaUser) {
        navigate('/profile-revelation');
      } else {
        navigate('/upgrade-experience');
      }
    } catch (error) {
      console.error('Onboarding save error:', error);
      toast.error('Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  // Render different step types
  const renderStep = () => {
    switch (step.type) {
      case 'welcome':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white text-2xl md:text-4xl mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.title}
            </motion.h1>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-1 mb-10"
            >
              {step.content.map((line, i) => (
                <p 
                  key={i} 
                  className={`text-[#A1A1AA] text-base md:text-lg leading-relaxed ${line === '' ? 'h-4' : ''}`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {line}
                </p>
              ))}
            </motion.div>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-[#D4AF37]/70 text-sm whitespace-pre-line mb-12"
            >
              {step.subtext}
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <Button
                onClick={handleNext}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="onboarding-start"
              >
                {step.button} <ArrowRight className="ml-3" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        );

      case 'textarea':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-white text-xl md:text-2xl mb-4 whitespace-pre-line"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.question}
            </motion.p>
            
            {step.subquestion && (
              <motion.p
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-[#71717A] text-base mb-6 whitespace-pre-line italic"
              >
                {step.subquestion}
              </motion.p>
            )}
            
            {step.guidance && (
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mb-6 space-y-1"
              >
                {step.guidance.map((g, i) => (
                  <p key={i} className="text-[#52525B] text-sm">— {g}</p>
                ))}
              </motion.div>
            )}
            
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <textarea
                value={formData[step.field]}
                onChange={(e) => setFormData(prev => ({ ...prev, [step.field]: e.target.value }))}
                placeholder={step.placeholder}
                className="w-full bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 rounded-none p-5 text-white text-base resize-none h-40 placeholder-[#3F3F46] outline-none transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid={`input-${step.field}`}
              />
            </motion.div>
            
            {step.help && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#52525B] text-xs mt-4 whitespace-pre-line"
              >
                {step.help}
              </motion.p>
            )}
          </motion.div>
        );

      case 'business_identity':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            {/* Question 1 - Identity Type */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question1}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {step.identityOptions.map((option, index) => (
                  <button
                    key={option.id}
                    onClick={() => setFormData(prev => ({ ...prev, identity_type: option.id }))}
                    className={`p-3 border text-center transition-all ${
                      formData.identity_type === option.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`identity-type-${option.id}`}
                  >
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
              
              {formData.identity_type === 'other' && (
                <motion.input
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  type="text"
                  value={formData.identity_type_other || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, identity_type_other: e.target.value }))}
                  placeholder="Précise..."
                  className="w-full mt-3 bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm placeholder-[#3F3F46] outline-none"
                />
              )}
            </motion.div>
            
            {/* Question 2 - Business Activity */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question2}
              </p>
              <textarea
                value={formData.business_activity}
                onChange={(e) => setFormData(prev => ({ ...prev, business_activity: e.target.value }))}
                placeholder={step.placeholder}
                className="w-full bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm resize-none h-24 placeholder-[#3F3F46] outline-none"
                data-testid="input-business-activity"
              />
            </motion.div>
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#D4AF37]/60 text-xs mt-6 whitespace-pre-line italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'business_situation':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            {/* Question 1 - Situation */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question1}
              </p>
              <div className="space-y-2">
                {step.situationOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleMultiSelect('situation', option.id)}
                    className={`w-full text-left p-3 border transition-all flex items-center gap-3 ${
                      formData.situation?.includes(option.id)
                        ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-white'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`situation-${option.id}`}
                  >
                    <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                      formData.situation?.includes(option.id)
                        ? 'border-[#D4AF37] bg-[#D4AF37]'
                        : 'border-[#3F3F46]'
                    }`}>
                      {formData.situation?.includes(option.id) && (
                        <Check size={10} className="text-black" />
                      )}
                    </div>
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
              
              {formData.situation?.includes('other') && (
                <motion.input
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  type="text"
                  value={formData.situation_other || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, situation_other: e.target.value }))}
                  placeholder="Précise..."
                  className="w-full mt-2 bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-3 text-white text-sm placeholder-[#3F3F46] outline-none"
                />
              )}
            </motion.div>
            
            {/* Question 2 - Revenue Level */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <p className="text-white text-base mb-3">{step.question2}</p>
              <div className="grid grid-cols-2 gap-2">
                {step.revenueOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setFormData(prev => ({ ...prev, revenue_level: option.id }))}
                    className={`p-3 border text-left transition-all ${
                      formData.revenue_level === option.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`revenue-${option.id}`}
                  >
                    <span className="text-xs">{option.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
            
            {/* Question 3 - Offer Type */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-white text-base mb-3">{step.question3}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {step.offerOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setFormData(prev => ({ ...prev, offer_type: option.id }))}
                    className={`p-3 border text-center transition-all ${
                      formData.offer_type === option.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`offer-${option.id}`}
                  >
                    <span className="text-xs">{option.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#D4AF37]/60 text-xs mt-6 whitespace-pre-line italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'multiselect':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-white text-xl md:text-2xl mb-8 whitespace-pre-line"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.question}
            </motion.p>
            
            <div className="space-y-3">
              {step.options.map((option, index) => (
                <motion.button
                  key={option.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 + (index * 0.05) }}
                  onClick={() => handleMultiSelect(step.field, option.id)}
                  className={`w-full text-left p-4 border transition-all flex items-center gap-3 ${
                    formData[step.field]?.includes(option.id)
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-white'
                      : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${
                    formData[step.field]?.includes(option.id)
                      ? 'border-[#D4AF37] bg-[#D4AF37]'
                      : 'border-[#3F3F46]'
                  }`}>
                    {formData[step.field]?.includes(option.id) && (
                      <Check size={12} className="text-black" />
                    )}
                  </div>
                  <span className="text-sm">{option.label}</span>
                </motion.button>
              ))}
            </div>
            
            {formData[step.field]?.includes('other') && (
              <motion.input
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                type="text"
                value={formData[`${step.field}_other`] || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, [`${step.field}_other`]: e.target.value }))}
                placeholder="Précise..."
                className="w-full mt-3 bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm placeholder-[#3F3F46] outline-none"
              />
            )}
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-[#D4AF37]/60 text-xs mt-6 whitespace-pre-line italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'select':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-white text-xl md:text-2xl mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.question}
            </motion.p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {step.options.map((option, index) => (
                <motion.button
                  key={option.id}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 + (index * 0.05) }}
                  onClick={() => setFormData(prev => ({ ...prev, [step.field]: option.id }))}
                  className={`p-4 border text-center transition-all ${
                    formData[step.field] === option.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <span className="text-sm">{option.label}</span>
                </motion.button>
              ))}
            </div>
            
            {formData[step.field] === 'other' && (
              <motion.input
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                type="text"
                value={formData[`${step.field}_other`] || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, [`${step.field}_other`]: e.target.value }))}
                placeholder="Précise..."
                className="w-full mt-3 bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm placeholder-[#3F3F46] outline-none"
              />
            )}
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#D4AF37]/60 text-xs mt-6 italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'positioning':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            {/* Question 1 - Text input */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question1}
              </p>
              <textarea
                value={formData.positioning_current}
                onChange={(e) => setFormData(prev => ({ ...prev, positioning_current: e.target.value }))}
                placeholder="Je pense être perçue comme..."
                className="w-full bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm resize-none h-24 placeholder-[#3F3F46] outline-none"
                data-testid="input-positioning-current"
              />
            </motion.div>
            
            {/* Question 2 - Multi-select */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question2}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {step.options.map((option, index) => (
                  <button
                    key={option.id}
                    onClick={() => handleMultiSelect('positioning_desired', option.id)}
                    className={`p-3 border text-center transition-all ${
                      formData.positioning_desired?.includes(option.id)
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`option-${option.id}`}
                  >
                    <span className="text-sm">{option.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
            
            {formData.positioning_desired?.includes('other') && (
              <motion.input
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                type="text"
                value={formData.positioning_other || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, positioning_other: e.target.value }))}
                placeholder="Précise..."
                className="w-full mt-3 bg-transparent border border-[#27272A] focus:border-[#D4AF37]/50 p-4 text-white text-sm placeholder-[#3F3F46] outline-none"
              />
            )}
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#D4AF37]/60 text-xs mt-6 whitespace-pre-line italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'content':
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-6"
            >
              {step.title}
            </motion.h2>
            
            {/* Question 1 - Instagram */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <p className="text-white text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {step.question1}
              </p>
              <div className="flex gap-4">
                {[{label: step.yesLabel || t('onboarding.content.yes'), value: true}, {label: step.noLabel || t('onboarding.content.no'), value: false}].map((option) => (
                  <button
                    key={option.label}
                    onClick={() => setFormData(prev => ({ ...prev, uses_instagram: option.value }))}
                    className={`px-8 py-3 border transition-all ${
                      formData.uses_instagram === option.value
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                    }`}
                    data-testid={`instagram-${option.value ? 'yes' : 'no'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
            
            {/* Show follow-up questions only if uses Instagram */}
            {formData.uses_instagram === true && (
              <>
                {/* Question 2 - Expression */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="mb-8"
                >
                  <p className="text-white text-base mb-4">{step.question2}</p>
                  <div className="space-y-2">
                    {step.expressionOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setFormData(prev => ({ ...prev, content_expression: option.id }))}
                        className={`w-full text-left p-3 border transition-all ${
                          formData.content_expression === option.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-white'
                            : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                        }`}
                      >
                        <span className="text-sm">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
                
                {/* Question 3 - Content status */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                >
                  <p className="text-white text-base mb-4">{step.question3}</p>
                  <div className="space-y-2">
                    {step.contentOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleMultiSelect('content_status', option.id)}
                        className={`w-full text-left p-3 border transition-all flex items-center gap-3 ${
                          formData.content_status?.includes(option.id)
                            ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-white'
                            : 'border-[#27272A] hover:border-[#3F3F46] text-[#A1A1AA]'
                        }`}
                      >
                        <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                          formData.content_status?.includes(option.id)
                            ? 'border-[#D4AF37] bg-[#D4AF37]'
                            : 'border-[#3F3F46]'
                        }`}>
                          {formData.content_status?.includes(option.id) && (
                            <Check size={10} className="text-black" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
            
            {step.microcopy && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#D4AF37]/60 text-xs mt-6 italic"
              >
                {step.microcopy}
              </motion.p>
            )}
          </motion.div>
        );

      case 'activation':
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"
            >
              <Sparkles className="text-[#D4AF37]" size={32} />
            </motion.div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white text-2xl md:text-4xl mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.title}
            </motion.h1>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-1 mb-8"
            >
              {step.content.map((line, i) => (
                <p 
                  key={i} 
                  className={`text-[#A1A1AA] text-base md:text-lg leading-relaxed ${line === '' ? 'h-4' : ''}`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {line}
                </p>
              ))}
            </motion.div>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-white text-lg whitespace-pre-line mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step.followup}
            </motion.p>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-[#D4AF37] text-sm italic mb-12"
            >
              {step.subtext}
            </motion.p>
            
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base disabled:opacity-50"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="onboarding-activate"
              >
                {loading ? 'Activation...' : step.button} <Sparkles className="ml-3" size={18} />
              </Button>
            </motion.div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex flex-col">
      {/* Progress bar - hidden on welcome and activation */}
      {step.type !== 'welcome' && step.type !== 'activation' && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-0.5 bg-[#1C1C1C]">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-[#D4AF37]/50 to-[#D4AF37]"
            />
          </div>
          <div className="bg-[#030303]/80 backdrop-blur-sm py-4 px-6">
            <p className="text-[#52525B] text-xs tracking-wide">
              Étape {currentStep} / {totalSteps - 1}
            </p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 flex items-center justify-center px-6 py-16 ${
        step.type !== 'welcome' && step.type !== 'activation' ? 'pt-24' : ''
      }`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation - hidden on welcome and activation */}
      {step.type !== 'welcome' && step.type !== 'activation' && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#030303]/90 backdrop-blur-sm border-t border-[#1C1C1C]">
          <div className="max-w-2xl mx-auto px-6 py-4 flex justify-between items-center">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="text-[#52525B] hover:text-white transition-colors disabled:opacity-30 text-sm"
            >
              ← {t('onboarding.navigation.back')}
            </button>
            
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-4 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="onboarding-next"
            >
              {t('onboarding.navigation.continue')} <ArrowRight className="ml-2" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
